import { useEffect, useState } from 'react';
import DropdownList from '../ui/Dropdown';
import {
  DUMMY_STOCK_DROPDOWN_MENU,
  DUMMY_STOCKS_ON_HAND,
} from '../../dummy_data/Dummy_Stock';
import { StockDropDownOption, StockOnHand } from '../../models/stock';
import { ActionType, TransactionType } from '../../constants/trade.constant';
import { NewTransactionDto } from '../../models/dto/transasctionDto';

const getTickerDisplayName = (tickerOption: StockDropDownOption) => {
  if (!tickerOption) return '';
  return tickerOption.ticker + ' - ' + tickerOption.company;
};

const AddNewTransactionCard = ({
  onCancelClick,
  submitTransaction,
}: PropsType) => {
  // Value should be coming from store
  const [tickerOptions, setTickerOptions] = useState<StockDropDownOption[]>([]);
  const [stockOwned, setStockOwned] = useState<StockOnHand[]>([]);

  // Dropdown fields
  const [tickerDropdownList, setTickerDropdownList] = useState<string[]>([]);

  // Form fields
  const [transactionType, setTransactionType] =
    useState<TransactionType | null>(null);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [quantity, setQuantity] = useState<number | ''>('');
  const [ticker, setTicker] = useState<string | null>(null);
  const [date, setDate] = useState<string>('');
  const [price, setPrice] = useState<number | ''>('');

  useEffect(() => {
    setTickerOptions(
      DUMMY_STOCK_DROPDOWN_MENU.sort((o1, o2) =>
        o1.ticker < o2.ticker ? -1 : o1.ticker > o2.ticker ? 1 : 0
      )
    );

    setStockOwned([...DUMMY_STOCKS_ON_HAND]);
  }, []);

  useEffect(() => {
    if (!transactionType) return;

    if (transactionType === 'Buy') {
      setTickerDropdownList(
        tickerOptions.map((option) => getTickerDisplayName(option))
      );
    } else {
      if (!actionType) setTickerDropdownList([]);
      else
        setTickerDropdownList(
          tickerOptions
            .filter((t) =>
              stockOwned.find(
                (s) => s.ticker === t.ticker && s.action === actionType
              )
            )
            .map((option) => getTickerDisplayName(option))
        );
    }
  }, [transactionType, actionType, tickerOptions, stockOwned]);

  const resetForm = () => {
    setQuantity('');
    setActionType(null);
    setTicker(null);
    setDate('');
    setPrice('');
  };

  const onSubmitTransaction = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Validation - check if it is not empty

    const selectedTicker = tickerOptions.find(
      (option) => getTickerDisplayName(option) === ticker
    )?.ticker;

    const newTransactionDto: NewTransactionDto = {
      transactionType: transactionType as TransactionType,
      actionType: actionType as ActionType,
      ticker: selectedTicker as string,
      quantity: quantity as number,
      price: price as number,
      date: new Date(date),
    };

    submitTransaction(newTransactionDto);
  };

  return (
    <form onSubmit={onSubmitTransaction}>
      <div className="space-y-4">
        <div className="border-b border-gray-900/10 pb-8">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Add New Transaction
          </h2>

          <div className="col-span-full mt-4">
            <label
              htmlFor="transactionType"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Transaction Type
            </label>
            <DropdownList
              name="transactionType"
              selected={transactionType}
              placeHolder={'Select'}
              onChange={(value) => {
                resetForm();
                if (value) setTransactionType(value);
              }}
              list={[TransactionType.BUY, TransactionType.SELL]}
            ></DropdownList>
          </div>
          <div className="col-span-full mt-4">
            <label
              htmlFor="actionType"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Action
            </label>
            <DropdownList
              name="actionType"
              selected={actionType}
              placeHolder={'Select'}
              onChange={(value) => {
                if (value) setActionType(value);
              }}
              list={[ActionType.Long, ActionType.Short]}
            ></DropdownList>
          </div>

          <div className="col-span-full mt-4">
            <label
              htmlFor="ticker"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Ticker
            </label>
            <DropdownList
              name="ticker"
              selected={ticker}
              placeHolder="Select"
              onChange={(value) => {
                if (value) setTicker(value);
              }}
              list={tickerDropdownList}
            ></DropdownList>
          </div>
          <div className="mt-4 grid col-span-full gap-x-6 gap-y-4">
            <div className="sm:col-span-3">
              <label
                htmlFor="quantity"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Quantity
              </label>
              <div className="mt-2">
                <input
                  type="number"
                  name="quantity"
                  id="quantity"
                  placeholder="0"
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(+e.target.value);
                  }}
                  autoComplete="quantity"
                  className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 grid col-span-full gap-x-6 gap-y-4">
            <div className="sm:col-span-3">
              <label
                htmlFor="price"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Transaction Price
              </label>
              <div className="mt-2">
                <input
                  type="number"
                  name="price"
                  placeholder="0"
                  id="price"
                  value={price}
                  onChange={(e) => {
                    setPrice(+e.target.value);
                  }}
                  autoComplete="title"
                  className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 grid col-span-full gap-x-6 gap-y-4">
            <div className="sm:col-span-3">
              <label
                htmlFor="price"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Transaction Date
              </label>
              <div className="mt-2">
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-center gap-x-4">
          <button
            type="button"
            onClick={onCancelClick}
            className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Submit
          </button>
        </div>
      </div>
    </form>
  );
};

type PropsType = {
  submitTransaction: (newTransactionDto: NewTransactionDto) => void;
  onCancelClick: () => void;
};

export default AddNewTransactionCard;
